import React from 'react'
import { Text } from '@csc/dls'
import FederalHolidaysArray from '@/data/federalHolidays.json'
import { useProduct } from '@/hooks'

type EstimatedArrivalProps = {
  variant: {
    estimated_min_delivery_days?: string;
    estimated_max_delivery_days?: string;
    is_digital?: boolean;
  }
}

const TIMEZONE_OFFSET = -5 * 60 * 60 * 1000 // Using 5 hours behind UTC, reasonable

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const toIsoDateOnlyNoTime = (date: Date) => new Date(date.getTime() + TIMEZONE_OFFSET).toISOString().slice(0, 10)
const FederalHoliday = FederalHolidaysArray
  .reduce((hash: { [key: string]: boolean; }, next: string) => {
    hash[next] = true
    return hash
  }, {})

const weekends = [true, false, false, false, false, false, true]

const isBusinessDay = (date: Date) => !FederalHoliday[toIsoDateOnlyNoTime(date)]
  && !weekends[date.getDay()]

const toEstimatedDateFormat = (date: Date) => `${months[date.getMonth()]} ${date.getDate()}`

const dayAdder = (startingDate: Date) => {
  const daysOut = [startingDate]
  return (businessDays: number) => {
    // This is the trick that lets us use a memory cache even
    // on SSR where the function is shared between sessions,
    // possibly spanning a day. We simply check if the starting date is
    // today, then splice the array cache out to today = 0 days out.
    const nowDate = toIsoDateOnlyNoTime(new Date())
    if (toIsoDateOnlyNoTime(daysOut[0]) !== nowDate) {
      daysOut.splice(0, daysOut.length, new Date(nowDate))
    }
    if (typeof daysOut[businessDays] === 'number') {
      return toEstimatedDateFormat(daysOut[businessDays])
    }
    const farthestPrecalculatedDay = daysOut[daysOut.length - 1]
    let date = farthestPrecalculatedDay
    let businessDaysNetOfPrecalculation = businessDays - daysOut.length
    while (businessDaysNetOfPrecalculation > 0) {
      date = new Date(date.getTime() + (1000 * 60 * 60 * 24))
      if (isBusinessDay(date)) {
        daysOut.push(date)
        businessDaysNetOfPrecalculation -= 1
      }
    }
    return toEstimatedDateFormat(date)
  }
}

// Currying in today's date to the array cache, DATE ONLY (hr:min:sec:ms all 0)
// We need this cache, otherwise, every instance of the JSX tag will cause
// significant looping - including multiple renders in, say, the same checkout session
// or a long cart page with many renderable instances of this.
const addDays = dayAdder(new Date(toIsoDateOnlyNoTime(new Date())))

const toInt = (n: string) => parseInt(n || '', 10)

export const arrivalWindow = ({
  variant: {
    estimated_min_delivery_days: earliest,
    estimated_max_delivery_days: latest,
    is_digital,
  } = {},
}: EstimatedArrivalProps) => {
  // because we do not do same-day, for now we do not need to
  // adjust by one day if it is after 5pm, say, local time.
  const [min, max] = [earliest, latest].map(toInt)
  return ([min, max].every(Number.isFinite) && !is_digital)
    ? `Est. Arrival: ${addDays(min)} - ${addDays(max)}`
    : ''
}

const EstimatedArrival: React.FC<EstimatedArrivalProps> = ({
  variant: {
    estimated_min_delivery_days,
    estimated_max_delivery_days,
  } = {},
}) => {
  const { product } = useProduct()
  const is_digital = !!product?.warranty.additional.is_digital
  const arrival = arrivalWindow({
    variant: {
      estimated_min_delivery_days,
      estimated_max_delivery_days,
      is_digital,
    },
  })
  return (!is_digital && arrival) ?
    <Text className="w-full text-center" size="sm">
      {arrival}
    </Text> :
    <div />
}

export default EstimatedArrival
